const resolveTagValue = (q, key) => {
  const chain = q?.tags?.module;
  if (!Array.isArray(chain)) return null;
  const entry = chain.find((t) => (t?.key || "").toLowerCase() === key);
  return entry?.value ?? null;
};

/**
 * Enriches each selected KPI with the data the Excel template needs (units,
 * default unit, required evidences, assets/sub-KPIs) plus its grouping tags
 * (module / category / subcategory). Shared by every template grouping
 * strategy so the per-KPI enrichment lives in one place.
 *
 * @param {object} input
 * @param {Array}  input.reportingQuestions - KPI questions from getReportingQuestionnew
 * @param {Array}  input.unitMasterList     - unit master rows
 * @param {Map}    input.mappingByKpi       - kpiId → { selectedUnitId }
 * @param {Array}  input.evidenceMappings   - { kpiId, requiredEvidences }
 * @param {Array}  input.assetRecords       - kpiAssetSettings rows
 * @param {number} input.sourceId           - location used to filter location-required assets
 * @returns {Array<{ group: { module: string|null, category: string|null, subCategory: string|null }, item: object }>}
 */
export const enrichTemplateKpis = ({
  reportingQuestions,
  unitMasterList,
  mappingByKpi,
  evidenceMappings,
  assetRecords,
  sourceId,
}) => {
  const unitMasterMap = new Map();
  for (const u of unitMasterList) {
    unitMasterMap.set(Number(u.id), { unit: u.unit, unit_name: u.unit_name });
  }

  const evidencesByKpi = new Map();
  for (const r of evidenceMappings) {
    evidencesByKpi.set(Number(r.kpiId), r.requiredEvidences ?? []);
  }

  const assetsByKpi = new Map();
  for (const a of assetRecords) {
    if (!a.isEnabled) continue;
    const locReq = !!(a.title?.isLocationRequired ?? a.isLocationRequired);
    if (locReq) {
      const assetSourceId = a.title?.sourceId ?? a.sourceId;
      if (Number(assetSourceId) !== Number(sourceId)) continue;
    }
    const kpiId = Number(a.title?.kpiId ?? a.kpiId);
    if (!assetsByKpi.has(kpiId)) assetsByKpi.set(kpiId, []);
    assetsByKpi.get(kpiId).push(a);
  }

  return reportingQuestions.map((q) => {
    const rawUnits = q.supportedUnits ?? [];
    const enrichedUnits = rawUnits
      .map((u) => {
        const m = unitMasterMap.get(Number(u.unit_id));
        return m ? { unit_id: Number(u.unit_id), unit: m.unit } : null;
      })
      .filter(Boolean);

    const baseUnit = rawUnits.find((u) => u.is_base) ?? rawUnits[0];
    const platformBaseUnitId = baseUnit ? Number(baseUnit.unit_id) : null;
    const mapping = mappingByKpi.get(Number(q.questionId));
    const selectedUnitId = mapping?.selectedUnitId ?? platformBaseUnitId;
    const defaultUnit =
      enrichedUnits.find((u) => u.unit_id === selectedUnitId)?.unit
      ?? enrichedUnits[0]?.unit
      ?? "";

    const lockedUnitId = mapping?.selectedUnitId ? Number(mapping.selectedUnitId) : null;
    const narrowToLockedUnit = (units) => {
      if (!lockedUnitId) return units;
      const locked = units.find((u) => u.unit_id === lockedUnitId);
      return locked ? [locked] : units;
    };
    const offeredUnits = narrowToLockedUnit(enrichedUnits);

    const assetsForKpi = assetsByKpi.get(Number(q.questionId)) ?? [];
    const enrichedAssets = assetsForKpi.map((a) => {
      const byId = new Map();
      for (const u of enrichedUnits) byId.set(u.unit_id, u);
      for (const u of a.supportedUnits ?? []) {
        if (byId.has(Number(u.unit_id))) continue;
        const master = unitMasterMap.get(Number(u.unit_id));
        if (master?.unit) byId.set(Number(u.unit_id), { unit_id: Number(u.unit_id), unit: master.unit });
      }
      const assetUnits = narrowToLockedUnit(Array.from(byId.values()));
      const assetDefaultUnit =
        assetUnits.find((u) => u.unit_id === selectedUnitId)?.unit
        ?? assetUnits[0]?.unit
        ?? "";
      const locReq = !!(a.title?.isLocationRequired ?? a.isLocationRequired);
      return {
        assetId: Number(a.id),
        title: String(a.title?.assetTitle ?? a.assetTitle ?? ""),
        kind: locReq ? "asset" : "subKpi",
        supportedUnits: assetUnits,
        defaultUnit: assetDefaultUnit,
      };
    });

    return {
      group: {
        module: resolveTagValue(q, "module"),
        category: resolveTagValue(q, "category"),
        subCategory: resolveTagValue(q, "subcategory"),
      },
      item: {
        questionId: q.questionId,
        title: q.title ?? "",
        supportedUnits: offeredUnits,
        defaultUnit,
        evidences: evidencesByKpi.get(Number(q.questionId)) ?? [],
        assets: enrichedAssets,
      },
    };
  });
};
